import { type JSX } from 'react';

import { EloBanner } from '@plurall/elo-v3';
import { useLocation } from '@tanstack/react-router';

import { type LocalNavigationProps } from '@/components';
import { LocalNavigationEntry } from '@/components/AppLayout/components/LocalNavigation/components/LocalNavigationEntry';
import {
  LocalNavigationContentFactory,
  LocalNavigationFactory,
} from '@/components/AppLayout/components/LocalNavigation/factories/LocalNavigation.factory';
import { usePerformLogout } from '@/hooks/queries/authentication/usePerformLogout';
import { useHomeRoute } from '@/hooks/useHomeRoute';
import { clsx } from '@/lib/formatters/classNames';

import { NavItemFactory, NavSectionFactory } from './components/factories/local-navigation-section-factory';
import { LocalNavigationLoadingItem } from './components/LocalNavigationLoadingItem';
import { LocalNavigationSection } from './components/LocalNavigationSection';
import { useLocalNavigationAccordion } from './hooks/useLocalNavigationAccordion';
import { useLocalNavigationMenuSections } from './hooks/useLocalNavigationMenuSections';
import { type UtilityAction } from './types/localNavigationActions';

export function LocalNavigation({ className = '', items, isOpen }: LocalNavigationProps): JSX.Element {
  const { pathname } = useLocation();
  const homeRoute = useHomeRoute();
  const { isExpanded, toggleAccordion } = useLocalNavigationAccordion();
  const { localNavigationSections, isLoadingMenu, hasMenuError } = useLocalNavigationMenuSections(items);
  const performLogout = usePerformLogout();

  const homeItem = {
    title: 'Início',
    text: 'Início',
    icon: 'home' as const,
    url: homeRoute,
    fragment: 'inicio',
    targetEnvironment: 'REACT',
  };

  const handleAction = async (action: UtilityAction): Promise<void> => {
    switch (action) {
      case 'open-ti-support':
        (window as Window & { showCollectorDialog?: () => void }).showCollectorDialog?.();
        return;
      case 'logout':
        await performLogout();
        return;
      default:
        return;
    }
  };

  return (
    <LocalNavigationFactory
      className={clsx('local-navigation', className)}
      data-open={String(isOpen)}
      data-testid='local-navigation'>
      <LocalNavigationContentFactory className='local-navigation__content'>
        <NavSectionFactory className='local-navigation__section'>
          <LocalNavigationEntry
            active={pathname === homeRoute}
            className='local-navigation__link'
            item={homeItem}
          />
        </NavSectionFactory>

        <LocalNavigationLoadingItem isLoadingMenu={isLoadingMenu} />

        {!isLoadingMenu && hasMenuError && (
          <NavItemFactory>
            <EloBanner
              width='100%'
              title='O menu não foi carregado corretamente. Por favor, atualize a página ou tente novamente mais tarde.'
              layout='multipleLines'
              status='error'
              variant='primary'
            />
          </NavItemFactory>
        )}
        {!isLoadingMenu &&
          !hasMenuError &&
          localNavigationSections.map((section, sectionIndex) => (
            <LocalNavigationSection
              key={section.title || `section-${sectionIndex}`}
              isExpanded={isExpanded}
              onAction={(action) => {
                void handleAction(action);
              }}
              section={section}
              toggleAccordion={toggleAccordion}
            />
          ))}
      </LocalNavigationContentFactory>
    </LocalNavigationFactory>
  );
}
